import React, { useCallback, useEffect, useRef, useState } from "react";
import AdminVideoScrollCaptureModal from "components/AdminVideoScrollCaptureModal";
import { IDLE_BUTTONS_HIDE_NOW_EVENT } from "contexts/IdleButtonsContext";
import { isAdminUser } from "utils/alertAudience";
import {
  ADMIN_VIDEO_SCROLL_DEFAULTS,
  getAdminVideoScrollPreview,
  startAdminVideoScroll,
} from "utils/adminVideoScrollEngine";

const STORAGE_KEY = "adminVideoScrollCaptureSettings";

const readStoredSettings = () => {
  if (typeof window === "undefined") return ADMIN_VIDEO_SCROLL_DEFAULTS;

  try {
    const rawValue = window.sessionStorage.getItem(STORAGE_KEY);
    return rawValue
      ? { ...ADMIN_VIDEO_SCROLL_DEFAULTS, ...JSON.parse(rawValue) }
      : ADMIN_VIDEO_SCROLL_DEFAULTS;
  } catch (error) {
    console.warn("Unable to read admin video scroll settings:", error);
    return ADMIN_VIDEO_SCROLL_DEFAULTS;
  }
};

const writeStoredSettings = (settings) => {
  if (typeof window === "undefined") return;

  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (error) {
    console.warn("Unable to save admin video scroll settings:", error);
  }
};

const isCaptureHotkey = (event) => (
  (event.ctrlKey || event.metaKey)
  && event.shiftKey
  && !event.altKey
  && String(event.key || "").toLowerCase() === "y"
);

const hasAdminVideoScrollAccess = (user) => (
  isAdminUser(user) || user?.settings?.admin === true
);

const hideIdleButtonsNow = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(IDLE_BUTTONS_HIDE_NOW_EVENT));
};

const SCROLL_START_DELAY_MS = 250;

export default function useAdminVideoScrollCapture(user) {
  const isAdmin = hasAdminVideoScrollAccess(user);
  const activeScrollRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState(readStoredSettings);
  const [error, setError] = useState("");
  const [isRunning, setIsRunning] = useState(false);

  useEffect(() => {
    if (!isAdmin) return undefined;

    const handleKeyDown = (event) => {
      if (!isCaptureHotkey(event)) return;

      event.preventDefault();
      setError("");
      setSettings(readStoredSettings());
      setIsOpen(true);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return undefined;

    const handleCancel = (event) => {
      if (event.key !== "Escape" || !activeScrollRef.current) return;
      activeScrollRef.current.cancel();
      activeScrollRef.current = null;
      setIsRunning(false);
    };

    window.addEventListener("keydown", handleCancel);
    return () => window.removeEventListener("keydown", handleCancel);
  }, [isAdmin]);

  useEffect(() => () => {
    if (activeScrollRef.current) {
      activeScrollRef.current.cancel();
      activeScrollRef.current = null;
    }
  }, []);

  const handleClose = useCallback(() => {
    if (isRunning) return;
    setIsOpen(false);
    setError("");
  }, [isRunning]);

  const handleRun = useCallback((nextSettings) => {
    if (!isAdmin || isRunning) return false;

    const mergedSettings = {
      ...ADMIN_VIDEO_SCROLL_DEFAULTS,
      ...nextSettings,
    };
    setSettings(mergedSettings);
    writeStoredSettings(mergedSettings);
    setError("");
    setIsOpen(false);
    setIsRunning(true);
    hideIdleButtonsNow();

    window.setTimeout(() => {
      const preview = getAdminVideoScrollPreview(mergedSettings);
      if (!preview.canScroll) {
        setError("Page has no scrollable content.");
        setIsOpen(true);
        setIsRunning(false);
        return;
      }

      const controller = startAdminVideoScroll(mergedSettings);
      activeScrollRef.current = controller;
      controller.done
        .catch((runError) => {
          setError(runError?.message || "Could not run the video scroll.");
          setIsOpen(true);
        })
        .finally(() => {
          if (activeScrollRef.current === controller) {
            activeScrollRef.current = null;
          }
          setIsRunning(false);
        });
    }, SCROLL_START_DELAY_MS);

    return true;
  }, [isAdmin, isRunning]);

  if (!isAdmin) {
    return null;
  }

  return (
    <AdminVideoScrollCaptureModal
      isOpen={isOpen}
      settings={settings}
      error={error}
      isRunning={isRunning}
      onClose={handleClose}
      onRun={handleRun}
    />
  );
}
